import React from 'react';
import DynamicStateService, { DynamicStateScopes } from '~features/DynamicStateService';
import { type SetupChecklistStateCompany, INITIAL_STATE_SETUP_CHECKLIST_COMPANY } from './SetupChecklist';

type DynamicStateCompanyWithSetupChecklist = {
  SetupChecklist?: SetupChecklistStateCompany;
};

export type SetupChecklistUpdateCompany = {
  state: Partial<SetupChecklistStateCompany>;
};

function useSetupChecklistState(): [
  SetupChecklistStateCompany,
  (update: SetupChecklistUpdateCompany['state']) => void
] {
  const [currentDynamicState, updateDynamicState] = DynamicStateService.useDynamicStateService<
    Record<string, never>,
    DynamicStateCompanyWithSetupChecklist
  >();

  const companySetupChecklist = currentDynamicState?.company?.SetupChecklist;

  const setupChecklistState: SetupChecklistStateCompany = React.useMemo(
    () => ({
      ...INITIAL_STATE_SETUP_CHECKLIST_COMPANY,
      ...companySetupChecklist,
    }),
    [
      companySetupChecklist?.sitemap,
      companySetupChecklist?.urlParameters,
      companySetupChecklist?.ignoredUrls,
      companySetupChecklist?.cacheExpiration,
      companySetupChecklist?.notFoundChecker,
    ]
  );

  const updateSetupChecklistState = React.useCallback(
    (update: SetupChecklistUpdateCompany['state']) => {
      updateDynamicState({
        scope: DynamicStateScopes.Company,
        state: { SetupChecklist: { ...update } },
      });
    },
    [updateDynamicState]
  );

  return [setupChecklistState, updateSetupChecklistState];
}

export default useSetupChecklistState;
